import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AIProductSearch } from './AIProductSearch';
import { AIProcessingStatus, AIProcessingStep } from './AIProcessingStatus';
import { ProductReviewForm } from './ProductReviewForm';
import { ProductSelectionDialog } from './ProductSelectionDialog';
import { llmService, ProductSearchResult, ImageSearchResult } from '../services/llmService';
import { productLibraryService } from '../services/productLibraryService';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';
import ImageProxyService from '../services/imageProxyService';

interface AIProductCreationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (productId: string) => void;
}

type CreationStep = 'search' | 'processing' | 'review';

export function AIProductCreationModal({
  open,
  onOpenChange,
  onSuccess,
}: AIProductCreationModalProps) {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<CreationStep>('search');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // AI search results
  const [productData, setProductData] = useState<ProductSearchResult | null>(null);
  const [productImages, setProductImages] = useState<ImageSearchResult[]>([]);
  const [selectedImage, setSelectedImage] = useState<ImageSearchResult | null>(null);

  // Processing status
  const [processingSteps, setProcessingSteps] = useState<AIProcessingStep[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);

  // Product selection
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [productVariations, setProductVariations] = useState<ProductSearchResult[]>([]);

  const resetModal = useCallback(() => {
    setCurrentStep('search');
    setIsProcessing(false);
    setSearchQuery('');
    setProductData(null);
    setProductImages([]);
    setSelectedImage(null);
    setProcessingSteps([]);
    setOverallProgress(0);
    setShowProductSelection(false);
    setProductVariations([]);
  }, []);

  const handleSearch = async (query: string, method: 'ai' | 'manual') => {
    if (method === 'manual') {
      // Skip to manual entry form
      setSearchQuery(query);
      setProductData({
        basic: {
          brand: '',
          model: query,
          partNumber: null,
          category: 'other',
          subcategory: null,
          description: '',
        },
        specifications: {
          dimensions: null,
          weight: null,
          power: null,
        },
        io: null,
        pricing: null,
        confidence: 0,
        provider: 'manual',
      });
      setCurrentStep('review');
      return;
    }

    setSearchQuery(query);
    setIsProcessing(true);

    try {
      // Step 1: ONLY search for product matches (no specs yet)
      logger.debug('Searching for products matching:', query);
      const patternMatches = await llmService.searchProductsByPattern(query);

      if (patternMatches && patternMatches.length > 0) {
        logger.debug(`Found ${patternMatches.length} matching products`);

        // Always show selection for user confirmation
        setProductVariations(patternMatches);
        setShowProductSelection(true);
        setIsProcessing(false);
        return;
      } else {
        // No matches found - go directly to manual entry
        logger.debug('No matches found, going to manual entry');
        toast.info('No matching products found. Please enter the product details manually.');

        // Parse what we can from the query
        const queryParts = query.split(' ');
        const possibleBrand = queryParts[0] || '';
        const possibleModel = queryParts.slice(1).join(' ') || query;

        setProductData({
          basic: {
            brand: possibleBrand,
            model: possibleModel,
            partNumber: null,
            category: 'other',
            subcategory: null,
            description: '',
          },
          specifications: {},
          io: null,
          pricing: null,
          confidence: 0.3,
          provider: 'manual',
        });
        setCurrentStep('review');
        setIsProcessing(false);
        return;
      }
    } catch (error) {
      logger.error('AI search failed:', error);
      toast.error('Failed to search for product. Please try again or use manual entry.');

      setProcessingSteps(prev =>
        prev.map(s =>
          s.status === 'processing' ? { ...s, status: 'error', error: 'Search failed' } : s,
        ),
      );

      setTimeout(() => {
        setCurrentStep('search');
        setIsProcessing(false);
      }, 2000);
    }
  };

  const handleImageSelection = () => {
    setCurrentStep('review');
  };

  const handleProductSelection = async (product: ProductSearchResult) => {
    setShowProductSelection(false);
    setCurrentStep('processing');
    setIsProcessing(true);

    // Initialize processing steps
    const steps: AIProcessingStep[] = [
      { id: 'specs', label: 'Fetching product specifications', status: 'processing' },
      { id: 'images', label: 'Finding product images', status: 'pending' },
      { id: 'validation', label: 'Validating information', status: 'pending' },
    ];
    setProcessingSteps(steps);
    setOverallProgress(33);

    try {
      // Now fetch real specifications from web sources
      const fullQuery = `${product.basic.brand} ${product.basic.model}`;
      logger.debug('Fetching real specifications for:', fullQuery);

      // Fetch specs from web sources
      const specsData = await llmService.fetchProductSpecs(
        product.basic.brand,
        product.basic.model,
        [(product as any).source_url].filter(Boolean),
      );

      logger.debug('Fetched specifications:', specsData);

      // Combine with basic product data
      const searchResult = {
        ...product,
        specifications: specsData.specifications || {},
        io: specsData.io || null,
        confidence: specsData.confidence || product.confidence,
      };

      logger.debug('Full product details:', searchResult);

      setProductData(searchResult);
      setOverallProgress(66);

      setProcessingSteps(prev =>
        prev.map(s =>
          s.id === 'specs' ? { ...s, status: 'complete', confidence: searchResult.confidence } : s,
        ),
      );

      // Search for images
      setProcessingSteps(prev =>
        prev.map(s => (s.id === 'images' ? { ...s, status: 'processing' } : s)),
      );

      let images: ImageSearchResult[] = [];
      if (!(searchResult as any).primary_image_url) {
        try {
          images = await llmService.searchProductImages(
            searchResult.basic.brand,
            searchResult.basic.model,
          );
        } catch (imageError) {
          logger.error('Image search error:', imageError);
        }
      } else {
        // Use primary image from search
        images = [
          {
            url: (searchResult as any).primary_image_url,
            source: 'Supabase Storage',
            confidence: 0.95,
            dimensions: { width: 800, height: 800 },
            type: 'product',
            angle: 'front',
          },
        ];
      }

      setProductImages(images);
      if (images.length > 0) {
        setSelectedImage(images[0]);
      }

      setOverallProgress(100);
      setProcessingSteps(prev =>
        prev.map(s =>
          s.id === 'images'
            ? {
                ...s,
                status: 'complete',
                confidence: images.length > 0 ? 0.9 : 0.5,
                label:
                  images.length > 0
                    ? `Found ${images.length} product images`
                    : 'No images found (manual upload available)',
              }
            : s.id === 'validation'
              ? { ...s, status: 'complete', confidence: product.confidence }
              : s,
        ),
      );

      setTimeout(() => {
        setCurrentStep('review');
        setIsProcessing(false);
      }, 1000);
    } catch (error) {
      logger.error('Failed to complete product selection:', error);
      toast.error('Failed to load product details');
      setCurrentStep('search');
      setIsProcessing(false);
    }
  };

  const handleSaveProduct = async (formData: any) => {
    logger.log('[AIProductCreationModal] handleSaveProduct called with:', formData);
    try {
      setIsProcessing(true);

      // Phase 2: Download and cache image permanently
      let finalImageUrl = selectedImage?.url;
      if (selectedImage?.url) {
        logger.log('Downloading and caching product image:', selectedImage.url);
        try {
          finalImageUrl = await ImageProxyService.downloadAndCache(selectedImage.url, {
            brand: formData.brand,
            model: formData.model,
          });
          logger.log('Image cached to Supabase Storage:', finalImageUrl);
        } catch (error) {
          logger.error('Failed to cache image, using original URL:', error);
          // Fallback to original URL already handled by downloadAndCache
        }
      }

      // Prepare product data with proper source tracking
      const productPayload = {
        ...formData,
        image_url: finalImageUrl,
        image_source: finalImageUrl?.includes('supabase.co')
          ? 'Supabase Storage'
          : selectedImage?.source,
        // Set source to track how product was created
        source: productData?.provider === 'manual' ? 'manual' : 'ai_generated',
        creation_method: productData?.provider === 'manual' ? 'manual' : 'ai_assisted',
        ai_confidence_score: productData?.confidence,
      };

      // Save to database - will automatically be user_global and cross-org
      const newProduct = await productLibraryService.create(productPayload);

      // Save additional images if any
      if (selectedImage && productImages.length > 1) {
        // TODO: Save additional images to product_images table
      }

      toast.success('Product created successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });

      if (onSuccess) {
        onSuccess(newProduct.id);
      }

      onOpenChange(false);
      resetModal();
    } catch (error) {
      logger.error('Failed to save product:', error);
      toast.error('Failed to save product. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getModalTitle = () => {
    switch (currentStep) {
      case 'search':
        return 'Create New Product';
      case 'processing':
        return 'AI Product Research';
      case 'review':
        return 'Review Product Information';
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={newOpen => {
          if (!newOpen) resetModal();
          onOpenChange(newOpen);
        }}
      >
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto border border-border/30 shadow-2xl rounded-xl"
          aria-describedby="ai-product-creation-description"
        >
          <DialogHeader>
            <DialogTitle>{getModalTitle()}</DialogTitle>
          </DialogHeader>
          <p id="ai-product-creation-description" className="sr-only">
            AI-powered product creation wizard for adding products to the library
          </p>
          <div className="space-y-6">
            {currentStep === 'search' && (
              <AIProductSearch onSearch={handleSearch} isSearching={isProcessing} />
            )}

            {currentStep === 'processing' && (
              <AIProcessingStatus
                steps={processingSteps}
                overallProgress={overallProgress}
                imageCount={productImages.length}
                imageSources={new Set(productImages.map(i => i.source)).size}
              />
            )}

            {currentStep === 'review' && productData && (
              <>
                <ProductReviewForm
                  initialData={productData}
                  selectedImage={selectedImage}
                  availableImages={productImages}
                  searchQuery={searchQuery}
                  onSave={handleSaveProduct}
                  onCancel={() => {
                    resetModal();
                    onOpenChange(false);
                  }}
                  onImageSelect={image => {
                    logger.debug('Image selected:', image);
                    setSelectedImage(image);
                  }}
                  isLoading={isProcessing}
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <ProductSelectionDialog
        open={showProductSelection}
        onOpenChange={setShowProductSelection}
        products={productVariations}
        onSelect={handleProductSelection}
        searchQuery={searchQuery}
      />
    </>
  );
}
